<template>
  <div class="container-responsive" style="padding-top: 1rem; padding-bottom: 1rem;">
    <!-- Notification Dialog -->
    <NotificationDialog
      v-model:visible="showNotification"
      :type="notificationType"
      :title="notificationTitle"
      :message="notificationMessage"
      @close="showNotification = false"
    />

    <v-card class="elevation-3" style="border-radius: 17px;">
      <v-card-title class="text-center" style="background-color: #002060; color: white; border-radius: 17px 17px 0 0;">
        <v-icon left>mdi-account-tie</v-icon>
        Client Details
      </v-card-title>

      <v-card-text class="pa-6">
    <!-- Top Navigation Bar -->
        <div class="d-flex justify-end mb-4">
      <v-btn 
        v-if="canEditClient"
            color="#002060"
            dark
            rounded
            size="large"
        @click="editClient"
        :disabled="loading"
            class="mr-2"
      >
        <v-icon left>mdi-pencil</v-icon>
        Edit Client
      </v-btn>

      <v-btn 
        v-if="canInviteMember"
            color="#002060"
            dark
            rounded
            size="large"
        @click="inviteAsMember"
        :disabled="loading"
            class="mr-2"
      >
        <v-icon left>mdi-account-plus</v-icon>
        Invite as Member
      </v-btn>
          
          <v-btn
            color="grey"
            dark
            rounded
            size="large"
            @click="$router.back()"
          >
            <v-icon left>mdi-arrow-left</v-icon>
            Back
        </v-btn>
      </div>

        <!-- Client Header -->
        <div class="text-center mb-6">
          <v-avatar size="80" color="#002060" class="mb-3">
              <v-icon size="40" color="white">mdi-account-tie</v-icon>
            </v-avatar>
          <h2 class="client-name mb-2">{{ client.firstName }} {{ client.surname }}</h2>
          <p class="client-id mb-3">CIF: {{ client.cifNumber || client.clientId }}</p>
        </div>

        <!-- Client Details Sections -->
        <v-row>
          <!-- Personal Information -->
          <v-col cols="12">
            <h3 class="section-title mb-4">Personal Information</h3>
          </v-col>
          
          <v-col cols="12" md="6">
            <v-text-field
              :model-value="client.firstName || 'N/A'"
              label="First Name"
              variant="solo"
              rounded
              flat
              hide-details
              prepend-inner-icon="mdi-account"
              readonly
            />
          </v-col>
          
          <v-col cols="12" md="6">
            <v-text-field
              :model-value="client.secondName || 'N/A'"
              label="Second Name"
              variant="solo"
              rounded
              flat
              hide-details
              prepend-inner-icon="mdi-account"
              readonly
            />
          </v-col>
          
          <v-col cols="12" md="6">
            <v-text-field
              :model-value="client.surname || 'N/A'"
              label="Surname"
              variant="solo"
              rounded
              flat
              hide-details
              prepend-inner-icon="mdi-account"
              readonly
            />
          </v-col>
          
          <v-col cols="12" md="6">
            <v-text-field
              :model-value="formattedIdNumber"
              label="ID Number"
              variant="solo"
              rounded
              flat
              hide-details
              prepend-inner-icon="mdi-card-account-details"
              readonly
            />
          </v-col>
          
          <v-col cols="12" md="6">
            <v-text-field
              :model-value="client.sarsNumber || 'N/A'"
              label="SARS Number"
              variant="solo"
              rounded
              flat
              hide-details
              prepend-inner-icon="mdi-identifier"
              readonly
            />
          </v-col>
        </v-row>

        <v-row>
          <!-- Contact Information -->
          <v-col cols="12">
            <h3 class="section-title mb-4">Contact Information</h3>
          </v-col>
          
          <v-col cols="12" md="6">
            <v-text-field
              :model-value="formattedPhone"
              label="Phone"
              variant="solo"
              rounded
              flat
              hide-details
              prepend-inner-icon="mdi-phone"
              readonly
            />
          </v-col>
          
          <v-col cols="12" md="6">
            <v-text-field
              :model-value="client.email || 'N/A'"
              label="Email"
              variant="solo"
              rounded
              flat
              hide-details
              prepend-inner-icon="mdi-email"
              readonly
            />
          </v-col>
        </v-row>

        <v-row>
          <!-- Address Information -->
          <v-col cols="12">
            <h3 class="section-title mb-4">Address Information</h3>
          </v-col>
          
          <v-col cols="12">
            <v-text-field
              :model-value="client.address || 'N/A'"
              label="Address"
              variant="solo"
              rounded
              flat
              hide-details
              prepend-inner-icon="mdi-home"
              readonly
            />
          </v-col>
          
          <v-col cols="12" md="6">
            <v-text-field
              :model-value="client.city || 'N/A'"
              label="City"
              variant="solo"
              rounded
              flat
              hide-details
              prepend-inner-icon="mdi-city"
              readonly
            />
          </v-col>
          
          <v-col cols="12" md="6">
            <v-text-field
              :model-value="client.postalCode || 'N/A'"
              label="Postal Code"
              variant="solo"
              rounded
              flat
              hide-details
              prepend-inner-icon="mdi-mailbox"
              readonly
            />
          </v-col>
        </v-row>

        <v-row>
          <!-- Membership Information -->
          <v-col cols="12">
            <h3 class="section-title mb-4">Membership Information</h3>
          </v-col>
          
          <v-col cols="12" md="6">
            <v-text-field
              :model-value="client.membership || 'N/A'"
              label="Membership Level"
              variant="solo"
              rounded
              flat
              hide-details
              prepend-inner-icon="mdi-star"
              readonly
            />
          </v-col>
          
          <v-col cols="12" md="6">
            <v-text-field
              :model-value="client.membershipNumber || 'N/A'"
              label="Membership Number"
              variant="solo"
              rounded
              flat
              hide-details
              prepend-inner-icon="mdi-card-membership"
              readonly
            />
          </v-col>
        </v-row>

        <v-row>
          <!-- Banking Information -->
          <v-col cols="12">
            <h3 class="section-title mb-4">Banking Information</h3>
          </v-col>
          
          <v-col cols="12" md="6">
            <v-text-field
              :model-value="client.bankName || 'N/A'"
              label="Bank Name"
              variant="solo"
              rounded
              flat
              hide-details
              prepend-inner-icon="mdi-bank"
              readonly
            />
          </v-col>
          
          <v-col cols="12" md="6">
            <v-text-field
              :model-value="client.bankAccountNumber || 'N/A'"
              label="Account Number"
              variant="solo"
              rounded
              flat
              hide-details
              prepend-inner-icon="mdi-credit-card"
              readonly
            />
          </v-col>
        </v-row>

        <v-row>
          <!-- System Information -->
          <v-col cols="12">
            <h3 class="section-title mb-4">System Information</h3>
          </v-col>
          
          <v-col cols="12" md="6">
            <v-text-field
              :model-value="client.dateAdded || 'N/A'"
              label="Date Added"
              variant="solo"
            rounded 
              flat
              hide-details
              prepend-inner-icon="mdi-calendar"
              readonly
            />
          </v-col>
          
          <v-col cols="12" md="6">
            <v-text-field
              :model-value="client.status || 'Active'"
              label="Status"
              variant="solo"
              rounded
              flat
              hide-details
              prepend-inner-icon="mdi-check-circle"
              readonly
            />
          </v-col>
        </v-row>

        <v-row v-if="client.notes">
          <!-- Additional Notes -->
          <v-col cols="12">
            <h3 class="section-title mb-4">Additional Notes</h3>
          </v-col>
          
          <v-col cols="12">
            <v-textarea
              :model-value="client.notes"
              label="Notes"
              variant="solo"
              rounded
              flat
              hide-details
              prepend-inner-icon="mdi-note-text"
              rows="3"
              readonly
            />
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import { db } from '../firebaseConfig';
import { doc, getDoc } from 'firebase/firestore';
import { ActivityLogger } from '../utils/activityLogger';
import NotificationDialog from '../components/NotificationDialog.vue';
import { formatPhoneNumber } from '../utils/phoneFormatter';
import { formatIdNumber } from '../utils/idNumberFormatter';

export default {
  name: 'ClientDetails',
  components: {
    NotificationDialog
  },
  data() {
    return {
      client: {},
      loading: true,
      error: null,
      showNotification: false,
      notificationType: 'success',
      notificationTitle: '',
      notificationMessage: ''
    };
  },
  computed: {
    canEditClient() {
      // Employee cannot edit client billing or membership levels
              // Only Admin and Executive can edit clients
        const userRole = this.$store.getters.userRole;
        return ['Admin', 'Executive'].includes(userRole);
    },
    canInviteMember() {
      // Only Admin and Executive can invite members
      const userRole = this.$store.getters.userRole;
      return ['Admin', 'Executive'].includes(userRole);
    },
    formattedPhone() {
      return formatPhoneNumber(this.client?.phone) || 'N/A';
    },
    formattedIdNumber() {
      return formatIdNumber(this.client?.idNumber) || 'N/A';
    }
  },
  methods: {
    async loadClient() {
      try {
        this.loading = true;
        this.error = null;
        
        const clientId = this.$route.params.id;
        if (!clientId) {
          this.error = 'No client ID provided';
          return;
        }

        console.log('Loading client with ID:', clientId);

        // Load from Firestore
        try {
          const clientDoc = await getDoc(doc(db, 'clients', clientId));
          if (clientDoc.exists()) {
            this.client = { id: clientId, ...clientDoc.data() };
            console.log('Client loaded from Firestore:', this.client);
            
            // Log client viewed activity
            const currentUser = this.$store.getters.user;
            const currentUserId = currentUser?.uid;
            const currentUserName = currentUser?.displayName || currentUser?.email?.split('@')[0] || 'Unknown';
            const clientName = `${this.client.firstName} ${this.client.surname}`;
            
            await ActivityLogger.clientViewed(currentUserName, currentUserId, clientName, 'Client Details Page');
          } else {
            console.error('Client document not found:', clientId);
            this.error = 'Client not found. The client may have been deleted.';
          }
        } catch (dbError) {
          console.error('Firestore error:', dbError);
          this.error = 'Failed to load client from database. Please try again.';
        }
      } catch (err) {
        console.error('Error loading client:', err);
        this.error = 'Failed to load client details';
      } finally {
        this.loading = false;
      }
    },
    

    
    getMembershipColor(membership) {
      switch (membership?.toLowerCase()) {
        case 'domestic': return 'green';
        case 'individual': return 'blue';
        case 'individual & domestic': return 'purple';
        default: return 'grey';
      }
    },
    
    getStatusColor(status) {
      switch (status?.toLowerCase()) {
        case 'active': return 'success';
        case 'inactive': return 'error';
        case 'pending': return 'warning';
        default: return 'success';
      }
    },
    
    formatClientType(clientType) {
      if (!clientType) return 'Individual';
      return clientType.charAt(0).toUpperCase() + clientType.slice(1);
    },
    
    formatDate(dateString) {
      if (!dateString) return 'N/A';
      try {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      } catch (error) {
        return dateString;
      }
    },
    
    editClient() {
      // Navigate to edit page based on client type
      const clientType = this.client.clientType || 'individual';
      let routeName = 'AddClient';
      
      switch (clientType) {
        case 'business':
          routeName = 'AddBusiness';
          break;
        case 'trust':
          routeName = 'AddTrust';
          break;
        default:
          routeName = 'AddClient';
      }
      
      // For now, navigate to the add form with the client data
      // In a real implementation, you'd have dedicated edit routes
      this.$router.push({ 
        name: routeName, 
        query: { 
          edit: 'true', 
          clientId: this.client.id 
        } 
      });
    },
    
    inviteAsMember() {
      // Navigate to invite member page with client data pre-filled
      const clientType = this.client.clientType || 'individual';
      
      // Determine member type based on client type
      let memberType = 'Individual';
      if (clientType === 'business') {
        memberType = 'Business';
      }
      
      // Navigate with query parameters to pre-fill the invite form
      this.$router.push({ 
        name: 'InviteMember', 
        query: { 
          fromClient: 'true',
          clientId: this.client.id,
          memberType: memberType,
          firstName: this.client.firstName || '',
          surname: this.client.surname || '',
          businessName: this.client.businessName || '',
          email: this.client.email || '',
          phone: this.client.phone || '',
          membershipType: this.client.membership || this.client.membershipType || ''
        } 
      });
    },

  },
  async created() {
    await this.loadClient();
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;700&display=swap');

* {
  font-family: 'Montserrat', sans-serif;
}

.section-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #002060;
  border-bottom: 2px solid #e5e7eb;
  padding-bottom: 0.5rem;
}

.client-name {
  font-size: 1.75rem;
  font-weight: 700;
  color: #002060;
  margin: 0;
}

.client-id {
  font-size: 1rem;
  color: #666;
  margin: 0;
  font-weight: 500;
}

.membership-chip {
  font-weight: 600;
  font-size: 1rem;
}

:deep(.v-field--variant-solo) {
  background-color: #f8f9fa !important;
}

:deep(.v-field--variant-solo .v-field__overlay) {
  background-color: #f8f9fa !important;
}
</style> 